import { useState, useEffect, useRef } from "react";
import getPartnerAsync, { getCustomFormFields } from "./helper";
import { logger } from "helpers/logger";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import * as Events from "config/events";
import PartnerReferSection from "./PartnerReferSection";
import useIsDesktop from "hooks/useIsDesktop";
import wrapperHOC from "@Utils/wrapperHOC";
import {
  openLeadFormModal as openLeadFormModalAction,
  updateOpeningSource,
} from "@Components/LeadFormModal/data/leadFormReducer";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { getWhitelabelDataSuccess, getPartnerCustomFormFieldConfig } from "@App/whiteLabelReducer";
import { getUtmParams } from "./utils";
import { useLocation } from "react-router";
import { AppState } from "@Store/index";
import useWhitelabel from "hooks/useWhitelabel";
import AmberLocalStorage from "@Utils/AmberlocalStorage";

const PartnerReferSectionWrapper = () => {
  const isDesktop = useIsDesktop();

  const [data, setData]: any = useState(null);
  const isMounted = useRef(true);
  const dispatch = useDispatch();
  const location = useLocation();
  const locationParams = new URLSearchParams(location.search);
  const { isWhitelabel, partner_uuid } = useWhitelabel();

  const { utmSource, utmMedium, campaign } = getUtmParams(locationParams);

  const utmMediumFromURL = locationParams?.get("utm_medium") || "";
  const campaignFromURL = locationParams?.get("utm_campaign") || "";

  const { openLeadFormModal } = useSelector(
    ({ leadFormModal }: AppState) => ({
      openLeadFormModal: leadFormModal.openLeadFormModal,
    }),
    shallowEqual,
  );

  const handleEnquire = () => {
    if (!openLeadFormModal) {
      dispatch(
        openLeadFormModalAction({
          inModal: true,
          page: 1,
        }),
      );
      dispatch(updateOpeningSource({ openingSource: "partner_enquire" }));
    }
  };

  const fetchCustomFormConfig = async (partnerId: string) => {
    try {
      const customFormConfigResponse = await getCustomFormFields(partnerId);
      if (customFormConfigResponse?.data?.data) {
        dispatch(getPartnerCustomFormFieldConfig(customFormConfigResponse.data.data));
      }
    } catch (error: any) {
      logger.log(error);
    }
  };

  const fetchData = async () => {
    try {
      if ((campaign === "partner" && utmSource && utmMedium) || isWhitelabel) {
        gtagEventEmitter({
          action: Events.PARTNER_LEAD_LANDING_PAGE,
          category: Events.PARTNER_LEAD_LANDING_PAGE,
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: campaign,
        });

        const storedUtmSource = isWhitelabel
          ? AmberLocalStorage.getItem("UTM_SOURCE") || partner_uuid
          : utmSource;

        const response = await getPartnerAsync(storedUtmSource, {
          url_path: "/api/v0/leads/partners",
          params: {
            methods: "parent_name,partner_description,partner_images,parent_canonical_name",
          },
        });

        const partnerData = response.data.data;

        if (!isMounted.current) return;
        setData(partnerData);

        if (!isWhitelabel && partnerData) {
          dispatch(getWhitelabelDataSuccess(partnerData));
          if (partnerData.is_partner_custom_form_enabled) {
            await fetchCustomFormConfig(partnerData.id);
          }
        }
      }
    } catch (error: any) {
      logger.log(error);
    }
  };

  useEffect(() => {
    if (data && utmMediumFromURL === "brand" && campaignFromURL === "partner") {
      handleEnquire();
    }
  }, [data, utmMedium, campaign]);

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => {
      isMounted.current = false;
    };
  }, []);

  if (!data) {
    return null;
  }

  return data.id ? <PartnerReferSection isDesktop={isDesktop} data={data} /> : <></>;
};

export default wrapperHOC(PartnerReferSectionWrapper, {
  componentName: "PartnerReferSectionWrapper-Home",
  showForChina: true,
});
